import { useEffect, useState } from 'react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyManager = { name: '', phone: '', email: '', password: '', is_active: true };

const countryOptions = [
  { code: '+27', flag: '🇿🇦', name: 'South Africa (+27)' },
  { code: '+91', flag: '🇮🇳', name: 'India (+91)' },
  { code: '+1', flag: '🇺🇸', name: 'USA (+1)' },
  { code: '+44', flag: '🇬🇧', name: 'UK (+44)' },
  { code: '+971', flag: '🇦🇪', name: 'UAE (+971)' },
  { code: '+61', flag: '🇦🇺', name: 'Australia (+61)' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore (+65)' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia (+966)' },
];

export default function CenterManagersPage() {
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sortOption, setSortOption] = useState('a-z');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState(emptyManager);
  const [countryCode, setCountryCode] = useState('+27');
  const [editingManager, setEditingManager] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Branch assignment state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [isPrimaryBranch, setIsPrimaryBranch] = useState(true);

  // Disable modal state
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [disableTargetManager, setDisableTargetManager] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [managersRes, branchesRes] = await Promise.all([
        api.get('/admin/center-managers'),
        api.get('/admin/branches')
      ]);
      setManagers(managersRes.data.data || []);
      setBranches(branchesRes.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load Center Managers');
    } finally {
      setLoading(false);
    }
  }

  const openAddModal = () => {
    setEditingManager(null);
    setForm(emptyManager);
    setCountryCode('+27');
    setIsAddModalOpen(true);
  };

  const openEditModal = (manager) => {
    setEditingManager(manager);

    let rawPhone = manager.phone || '';
    let foundCode = '+27';
    let digitsOnly = rawPhone.replace(/\D/g, '');

    for (const option of countryOptions) {
      const cCodeDigits = option.code.replace('+', '');
      if (rawPhone.startsWith(option.code) || digitsOnly.startsWith(cCodeDigits)) {
        foundCode = option.code;
        if (digitsOnly.startsWith(cCodeDigits)) {
          digitsOnly = digitsOnly.slice(cCodeDigits.length);
        }
        break;
      }
    }

    setCountryCode(foundCode);
    setForm({
      name: manager.name || '',
      phone: digitsOnly.slice(0, 10),
      email: manager.email || '',
      password: '',
      is_active: Number(manager.is_active) === 1,
    });
    setIsAddModalOpen(true);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingManager(null);
    setForm(emptyManager);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!form.email || !form.email.trim()) {
      setError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError('Please register with a valid email address (e.g. manager@example.com)');
      return;
    }

    let finalPhone = '';
    if (form.phone) {
      const cleanPhone = form.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit mobile phone number');
        return;
      }
      finalPhone = `${countryCode}${cleanPhone}`;
    }

    try {
      const payload = {
        name: form.name.trim(),
        phone: finalPhone || undefined,
        email: form.email ? form.email.trim() : undefined,
        password: form.password ? form.password : undefined,
        is_active: form.is_active ? 1 : 0,
      };

      if (editingManager) {
        await api.put(`/admin/center-managers/${editingManager.id}`, payload);
        setSuccessMessage('Center Manager updated successfully');
      } else {
        await api.post('/admin/center-managers', payload);
        setSuccessMessage('Center Manager created successfully and credentials sent to email');
      }

      closeModal();
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save Center Manager');
    }
  };

  const toggleStatus = async (manager) => {
    setError('');
    setSuccessMessage('');
    try {
      const newStatus = Number(manager.is_active) === 1 ? 0 : 1;
      await api.put(`/admin/center-managers/${manager.id}`, { is_active: newStatus });
      setSuccessMessage(`Center Manager ${newStatus ? 'enabled' : 'disabled'} successfully`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  };

  const handleDisableClick = (manager) => {
    if (manager.is_active === 1 && manager.assigned_branches && manager.assigned_branches.length > 1) {
      setDisableTargetManager(manager);
      setIsDisableModalOpen(true);
    } else {
      toggleStatus(manager);
    }
  };

  const handleResendCredentials = async (manager) => {
    if (!window.confirm(`Are you sure you want to resend login credentials email to "${manager.name}" (${manager.email})? This will generate a new password and email it.`)) return;
    setError('');
    setSuccessMessage('');
    try {
      const res = await api.post(`/admin/center-managers/${manager.id}/resend-credentials`);
      const newPass = res.data?.new_password || '';
      setSuccessMessage(`Credentials email sent to ${manager.email} successfully${newPass ? ` (New Password: ${newPass})` : ''}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to send credentials email');
    }
  };

  const openAssignModal = (manager) => {
    setSelectedManager(manager);
    setSelectedBranchId('');
    setIsPrimaryBranch(true);
    setIsAssignModalOpen(true);
  };

  const handleAssignBranch = async (event) => {
    event.preventDefault();
    if (!selectedBranchId) {
      setError('Please select a branch to assign');
      return;
    }
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/center-managers/assign-branch', {
        manager_id: selectedManager.id,
        branch_id: parseInt(selectedBranchId),
        is_primary: isPrimaryBranch
      });
      const branchObj = branches.find(b => b.id === parseInt(selectedBranchId));
      const branchName = branchObj ? branchObj.name : 'Branch';
      setSuccessMessage(`Branch "${branchName}" assigned to ${selectedManager.name} successfully`);
      setIsAssignModalOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to assign branch');
    }
  };

  const handleToggleBranchStatus = async (managerId, branchId, newStatus) => {
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/center-managers/toggle-branch-status', {
        manager_id: managerId,
        branch_id: branchId,
        is_active: newStatus
      });
      setSuccessMessage(`Branch access ${newStatus === 1 ? 'enabled' : 'disabled'} successfully`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update branch status');
    }
  };

  const sortedManagers = sortItems(managers, sortOption, 'name', 'created_at');

  const filteredManagers = sortedManagers.filter((m) => {
    const isActive = Number(m.is_active) === 1;
    if (statusFilter === 'active' && !isActive) return false;
    if (statusFilter === 'inactive' && isActive) return false;

    if (branchFilter) {
      const hasBranch = (m.assigned_branches || []).some(b => String(b.branch_id) === String(branchFilter));
      if (!hasBranch) return false;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const matchName = m.name?.toLowerCase().includes(q);
      const matchPhone = m.phone?.toLowerCase().includes(q);
      const matchEmail = m.email?.toLowerCase().includes(q);
      if (!matchName && !matchPhone && !matchEmail) return false;
    }

    return true;
  });

  return (
    <div>
      <PageHeader
        title="Center Manager Management"
        subtitle="Manage accounts and assign operational branch scopes"
        action={
          <button type="button" className="primary-button" onClick={openAddModal} style={{ whiteSpace: 'nowrap' }}>
            + Add Manager
          </button>
        }
      />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <select
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 14, minWidth: 180, cursor: 'pointer', background: '#ffffff'
          }}
        >
          <option value="">All Branches</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 14, minWidth: 180, cursor: 'pointer', background: '#ffffff'
          }}
        >
          <option value="all">All Statuses (Active & Disabled)</option>
          <option value="active">Active Only</option>
          <option value="inactive">Disabled Only</option>
        </select>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search manager by name, phone, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px 8px 34px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
              minWidth: 240,
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              background: '#ffffff'
            }}
          />
          <span style={{ position: 'absolute', left: 10, color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>
            🔍
          </span>
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              ×
            </button>
          )}
        </div>

        <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
      </div>

      {/* Add / Edit Manager Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeModal} title={editingManager ? 'Edit Center Manager' : 'Create Center Manager Account'}>
        <form onSubmit={handleSubmit}>
          <label>
            Full Name
            <input placeholder="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </label>

          <label>
            Phone Number (10 digits)
            <div style={{ display: 'flex', gap: 6 }}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{ width: 110, padding: '8px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc', cursor: 'pointer' }}
              >
                {countryOptions.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="10-digit phone number"
                maxLength={10}
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                style={{ flex: 1 }}
              />
            </div>
            {form.phone && (
              <span style={{ fontSize: 11, color: form.phone.length === 10 ? '#16a34a' : '#dc2626', marginTop: 3, display: 'block', fontWeight: 500 }}>
                {form.phone.length === 10 ? '✓ Valid 10-digit number' : `Entered ${form.phone.length}/10 digits`}
              </span>
            )}
          </label>

          <label>
            Email Address
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required />
          </label>

          <label className="form-checkbox" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              name="is_active"
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#7c3aed' }}
            />
            <span style={{ fontSize: '14px', fontWeight: 600, color: form.is_active ? '#15803d' : '#b91c1c' }}>
              Active Account
            </span>
          </label>

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={closeModal}>Cancel</button>
            <button type="submit" className="primary-button">{editingManager ? 'Update Manager' : 'Create Manager'}</button>
          </div>
        </form>
      </Modal>

      {/* Assign Branch Modal */}
      <Modal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} title={`Assign Branch to "${selectedManager?.name}"`}>
        <form onSubmit={handleAssignBranch}>
          <label>
            Center Manager
            <input
              type="text"
              value={selectedManager?.name || ''}
              disabled
              style={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, cursor: 'not-allowed' }}
            />
          </label>

          <label>
            Select Branch to Assign
            <select value={selectedBranchId} onChange={(e) => setSelectedBranchId(e.target.value)} required style={{ padding: '8px 12px', width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', marginTop: 4 }}>
              <option value="">Choose a branch</option>
              {branches
                .filter(b => !selectedManager?.assigned_branches?.some(ab => ab.branch_id === b.id))
                .map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))
              }
            </select>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <input type="checkbox" checked={isPrimaryBranch} onChange={(e) => setIsPrimaryBranch(e.target.checked)} />
            Set as Primary Branch
          </label>

          <div className="modal-actions" style={{ marginTop: 24 }}>
            <button type="button" className="secondary-button" onClick={() => setIsAssignModalOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Assign Branch</button>
          </div>
        </form>
      </Modal>

      {/* Disable Branch/Account Options Modal */}
      <Modal
        isOpen={isDisableModalOpen}
        onClose={() => { setIsDisableModalOpen(false); setDisableTargetManager(null); }}
        title={`Disable Access — ${disableTargetManager?.name}`}
      >
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 14, color: '#475569', marginBottom: 16 }}>
            <strong>{disableTargetManager?.name}</strong> is currently assigned to <strong>{disableTargetManager?.assigned_branches?.length} branches</strong>. Select an option:
          </p>

          <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#1e293b' }}>1. Enable / Disable Specific Branch Access:</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {disableTargetManager?.assigned_branches?.map((b) => {
                const isDisabled = Number(b.is_active) === 0;
                return (
                  <div
                    key={b.branch_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      background: isDisabled ? '#fff5f5' : '#ffffff',
                      border: `1.5px solid ${isDisabled ? '#fca5a5' : '#cbd5e1'}`,
                      borderRadius: 8
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: isDisabled ? '#dc2626' : '#334155' }}>
                      📍 {b.name} {b.is_primary ? '★' : ''} {isDisabled ? '(Currently Disabled)' : ''}
                    </span>
                    <button
                      type="button"
                      className="secondary-button"
                      style={{
                        fontSize: 12,
                        padding: '6px 12px',
                        color: isDisabled ? '#16a34a' : '#dc2626',
                        borderColor: isDisabled ? '#86efac' : '#fca5a5',
                        fontWeight: 600
                      }}
                      onClick={async () => {
                        await handleToggleBranchStatus(disableTargetManager.id, b.branch_id, isDisabled ? 1 : 0);
                        setIsDisableModalOpen(false);
                        setDisableTargetManager(null);
                      }}
                    >
                      {isDisabled ? 'Enable This Branch' : 'Disable This Branch'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 16 }}>
            <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#991b1b' }}>2. Disable Entire Manager Account:</h4>
            <p style={{ fontSize: 13, color: '#7f1d1d', margin: '0 0 12px' }}>
              This will suspend the user account completely across all branches.
            </p>
            <button
              type="button"
              className="primary-button"
              style={{ background: '#dc2626', borderColor: '#dc2626' }}
              onClick={async () => {
                await toggleStatus(disableTargetManager);
                setIsDisableModalOpen(false);
                setDisableTargetManager(null);
              }}
            >
              Disable Entire Account
            </button>
          </div>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => { setIsDisableModalOpen(false); setDisableTargetManager(null); }}
          >
            Cancel
          </button>
        </div>
      </Modal>



      {loading ? (
        <div>Loading center managers...</div>
      ) : filteredManagers.length === 0 ? (
        <div>No center managers found.</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Assigned Branches</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredManagers.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600 }}>{m.name}</td>
                  <td>{m.email}</td>
                  <td>{m.phone || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {m.assigned_branches && m.assigned_branches.length > 0 ? (
                        m.assigned_branches.map((b) => {
                          const isDisabled = Number(b.is_active) === 0;
                          return (
                            <span key={b.branch_id} style={{
                              padding: '3px 10px',
                              background: isDisabled ? '#fee2e2' : (b.is_primary ? '#ede9fe' : '#f3f4f6'),
                              color: isDisabled ? '#dc2626' : (b.is_primary ? '#7c3aed' : '#4b5563'),
                              border: isDisabled ? '1px solid #fca5a5' : (b.is_primary ? '1px solid #ddd6fe' : '1px solid #e5e7eb'),
                              borderRadius: 12,
                              fontSize: 12,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontWeight: isDisabled ? 700 : 500
                            }}>
                              {b.name} {b.is_primary ? '★' : ''} {isDisabled ? '(Disabled)' : ''}
                              <button
                                type="button"
                                onClick={() => handleToggleBranchStatus(m.id, b.branch_id, isDisabled ? 1 : 0)}
                                style={{
                                  border: 'none',
                                  background: 'none',
                                  color: isDisabled ? '#16a34a' : '#ef4444',
                                  cursor: 'pointer',
                                  padding: '0 2px',
                                  fontSize: 12,
                                  fontWeight: 700
                                }}
                                title={isDisabled ? "Enable branch access" : "Disable branch access"}
                              >
                                {isDisabled ? '✓ Enable' : '×'}
                              </button>
                            </span>
                          );
                        })
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: 13 }}>No branches assigned</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const isActive = Number(m.is_active) === 1;
                      return (
                        <button
                          type="button"
                          onClick={() => handleDisableClick(m)}
                          title={`Click to set as ${isActive ? 'Disabled' : 'Active'}`}
                          style={{
                            border: 'none',
                            borderRadius: '16px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
                            color: isActive ? '#15803d' : '#b91c1c',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <span style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: isActive ? '#22c55e' : '#ef4444'
                          }} />
                          {isActive ? 'Active' : 'Disabled'}
                        </button>
                      );
                    })()}
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="secondary-button"
                        style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => openEditModal(m)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px', fontSize: '12px', borderRadius: '6px',
                          border: '1px solid #fed7aa', backgroundColor: '#fff7ed', color: '#c2410c',
                          cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleResendCredentials(m)}
                        title={`Resend login credentials email to ${m.email}`}
                      >
                        ✉️ Resend Credentials
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px', fontSize: '12px', borderRadius: '6px',
                          border: '1px solid #c7d2fe', backgroundColor: '#e0e7ff', color: '#4338ca',
                          cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => openAssignModal(m)}
                        title={`Assign a branch to ${m.name}`}
                      >
                        🔗 Assign Branch
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          borderRadius: '6px',
                          border: Number(m.is_active) === 1 ? '1px solid #fca5a5' : '1px solid #86efac',
                          backgroundColor: Number(m.is_active) === 1 ? '#fef2f2' : '#f0fdf4',
                          color: Number(m.is_active) === 1 ? '#dc2626' : '#16a34a',
                          cursor: 'pointer',
                          fontWeight: 500,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleDisableClick(m)}
                      >
                        {Number(m.is_active) === 1 ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
